<template>
  <div class="sessions-search">
    <m-ais-wrapper
      index="sessions"
      :query-fields="queryFields"
      class="sessions-search-wrapper"
      initial-sort-by="startTimeStamp:asc"
    >
      <ais-configure :hits-per-page.camel="9" :filters="todayAndFutureFilter()" />

      <div :class="contentClasses">
        <!-- Avoid using streamContainerKey which might cause reactivity issues -->
        <m-ais-stream-container :grid="isGridView">
          <template v-slot:prepend
            ><!-- Top section with search and filter controls -->
            <div class="m-top-section pt-4">
              <div class="m-top-section__left-container">
                <m-ais-search-box :placeholder="$t('search.placeholder_sessions')" />
              </div>
              <div class="m-top-section__right-container">
                <ais-clear-refinements class="filter-toggle" @click.native="toggleFilters">
                  <template v-slot="{ canRefine }">
                    <div>
                      <v-icon color="primary">{{ canRefine ? 'mdi-filter' : 'mdi-filter-outline' }}</v-icon>
                      <span>{{ $t('filters.showFilters') }}</span>
                    </div>
                  </template>
                </ais-clear-refinements>
                <div class="ml-auto d-flex flex-row">
                  <div class="view-toggle">
                    <a sentry-tag="sessions-search.button.enableGridView" @click.prevent="enableGridView(true)">
                      <svg
                        :fill="isGridView ? getSelectedIconColor : getUnSelectedIconColor"
                        height="24"
                        width="24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g>
                          <path
                            d="M10.5 0H.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V.5a.5.5 0 0 0-.5-.5ZM23.5 0h-10a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V.5a.5.5 0 0 0-.5-.5ZM10.5 13H.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5v-10a.5.5 0 0 0-.5-.5Z"
                          />
                          <path
                            d="M10.5 0H.5a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V.5a.5.5 0 0 0-.5-.5ZM23.5 13h-10a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5v-10a.5.5 0 0 0-.5-.5Z"
                          />
                        </g>
                      </svg>
                    </a>

                    <a sentry-tag="sessions-search.button.disableGridView" @click.prevent="enableGridView(false)">
                      <svg fill="none" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                        <g :fill="!isGridView ? getSelectedIconColor : getUnSelectedIconColor" clip-path="url(#a)">
                          <path
                            d="M23.5 9H.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h23a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5ZM23.5 1H.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h23a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5ZM23.5 17H.5a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h23a.5.5 0 0 0 .5-.5v-5a.5.5 0 0 0-.5-.5Z"
                          />
                        </g>
                      </svg>
                    </a>
                  </div>
                  <!-- Sort selector with fixed sort options that exist in the schema -->
                  <!-- <ais-sort-by
                    :items="[
                      { value: 'timestamp:desc', label: $t('search.sort.date_desc') },
                      { value: 'timestamp:asc', label: $t('search.sort.date_asc') },
                      { value: 'title:asc', label: $t('search.sort.title_asc') },
                      { value: 'title:desc', label: $t('search.sort.title_desc') },
                    ]"
                    class="sort-by ml-3"
                  /> -->
                </div>
              </div>
            </div>
          </template>
          <template v-slot:append-hit />
          <!-- Results area -->

          <template v-slot:item="{ item }">
            <m-community-session-card
              :session="makeSessionEntity(item)"
              :layout="isGridView ? 'grid' : 'list'"
              :onCardClick="sessionCardClicked"
            />
          </template>
        </m-ais-stream-container>
      </div>

      <!-- Filters sidebar -->
      <m-stream-filters v-model="isFiltersVisible" :type="FilterType.SESSIONS" />
      <router-view />
    </m-ais-wrapper>

    <!-- Session detail dialog -->
    <m-community-session-dialog
      :sessionId="selectedCommunitySessionId"
      :open="detailsDialogOpen"
      :onClose="closeDetailsDialog"
    />
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import { UsersState } from '@/store/users';
import { ICommunitySession } from '@mentessa/types';
import { MCommunitySessionCard } from '@/components/Cards/MCommunitySessionCard';
import { MAisSearchBox, MAisWrapper, MAisStreamContainer } from '@/components/MAIS';
import MStreamFilters, { FilterType } from '@/components/MStreamFilters';
import { RouteName } from '@/router/types';
import MCommunitySessionDialog from '@/components/Dialogs/MCommunitySessionDialog';

export default Vue.extend({
  name: 'm-sessions-stream-search',

  components: {
    MCommunitySessionCard,
    MAisWrapper,
    MAisSearchBox,
    MStreamFilters,
    MAisStreamContainer,
    MCommunitySessionDialog,
  },

  props: {
    queryFields: {
      type: String,
      default: 'title,description,organizerName,topics',
    },
  },

  data: () => ({
    createSessionDialogOpen: false,
    isFiltersVisible: false,
    isGridView: true,
    loading: false,
    view: 'grid',
    selectedCommunitySessionId: null,
    detailsDialogOpen: false,
  }),

  computed: {
    ...mapState<UsersState>('users', {
      currentUser: (state: UsersState) => state.me,
    }),

    ...mapGetters('tenant', {
      isCommunitySessionsEnabled: 'isCommunitySessionsEnabled',
    }),

    canCreateSession() {
      return (
        this.currentUser?.attributes?.roles?.includes('Admin') ||
        this.currentUser?.attributes?.roles?.includes('Organizer')
      );
    },

    contentClasses() {
      return {
        'm-main-container': true,
        'm-main-container--with-filter': this.isFiltersVisible && this.$vuetify.breakpoint.lgAndUp,
      };
    },

    getSelectedIconColor() {
      return this.$vuetify.theme.themes.light.primary;
    },

    getUnSelectedIconColor() {
      return this.$vuetify.theme.themes.light.lightgray;
    },

    // Expose the FilterType enum to the template
    FilterType() {
      return FilterType;
    },
  },

  methods: {
    toggleFilters() {
      this.isFiltersVisible = !this.isFiltersVisible;
    },

    async sessionCardClicked(session) {
      this.selectedCommunitySessionId = session.id;
      this.detailsDialogOpen = true;
      this.$router.push({
        name: RouteName.CommunitySessions,
        query: { session: `${session.id}` },
      });
    },
    closeDetailsDialog() {
      this.detailsDialogOpen = false;
      this.$router.push({
        name: RouteName.CommunitySessions,
      });
    },

    enableGridView(enabled: boolean) {
      if (this.isGridView !== enabled) {
        this.isGridView = enabled;
      }
    },

    todayAndFutureFilter() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Convert to Unix timestamp (seconds since epoch)
      const todayTimestamp = Math.floor(today.getTime() / 1000);
      return `startTimeStamp:>=${todayTimestamp}`;
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    makeSessionEntity(searchItem: any): ICommunitySession {
      return {
        id: parseInt(searchItem.sessionId),
        tenant: {
          id: searchItem.tenantId,
        },
        title: searchItem.title,
        description: searchItem.description,
        meetingUrl: searchItem.meetingUrl,
        bannerImage: searchItem.bannerImage,
        maximumAttendees: searchItem.maximumAttendees,
        status: searchItem.status,
        language: searchItem.language || '',
        date: new Date(searchItem.date),
        startTime: new Date(searchItem.startTime),
        endTime: new Date(searchItem.endTime),
        timeZone: searchItem.timeZone,
        createdBy: {
          id: searchItem.organizerId,
          identity: {
            attributes: {
              firstName: searchItem.organizerName?.split(' ')[0] || '',
              lastName: searchItem.organizerName?.split(' ').slice(1).join(' ') || '',
            },
          },
        },
        participants: [],
        topics: searchItem.topics?.map((topic) => ({ name: topic })) || [],
        attributes: {
          roles: searchItem.roles || [],
        },
        createdAt: new Date(searchItem.timestamp),
      } as ICommunitySession;
    },

    // async openSessionDetails(hit) {
    //       if (this.$route.name !== RouteName.CommunitySession Details) {
    //   await this.$router.push({ name: RouteName.CommunitySessionDetails, params: { userId: hit.use      rId } });
    //       }
    // },
  },
});
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/_variables';

.sessions-search {
  width: 100%;
}

.sessions-search-wrapper {
  width: 100%;
  display: flex;
}

.m-main-container {
  max-width: 1126px;
  width: 100%;

  &--with-filter {
    margin-right: 320px;
  }
}

.m-top-section {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 10px;

  @media #{map-get($display-breakpoints, 'xs-only')} {
    flex-direction: column;
  }

  .m-top-section__right-container {
    flex: 1;
    display: flex;
  }

  .m-top-section__left-container {
    flex: 1;
  }
}

.sessions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}

.filter-toggle {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 51px;
  max-width: 94px;
  border-radius: 6px;
  border: 1px solid var(--v-beige-base);
  box-shadow: 0 0 1px var(--v-beige-base);
  padding: 0 16px;
  cursor: pointer;
  width: 100%;
  background-color: var(--v-white-base);

  span {
    font-size: 14px;
    font-weight: 500;
  }
}

.view-toggle {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 51px;
  max-width: 90px;
  border-radius: 6px;
  border: 1px solid var(--v-beige-base);
  padding: 0 16px;
  width: 100%;
  background-color: var(--v-white-base);

  a {
    display: block;
    font-size: 0;
  }

  a + a {
    margin-left: 16px;
  }
}

.sort-by {
  height: 51px;
  border-radius: 6px;
  border: 1px solid var(--v-beige-base);
  box-shadow: 0 0 1px var(--v-beige-base);
  padding: 0 16px;
  background-color: var(--v-white-base);
  display: flex;
  align-items: center;
}
</style>
